/**
 * Meetings — ADR-001 §7.3 (`meetings`, `meeting_media`, `meeting_decisions`, `meeting_tasks`), §9
 * (`llm.summarise_meeting`), §10.2, §11.10; ADR-004 (`meeting_summary`: the model proposes, a person confirms); the
 * prototype's MEET / newMeet / saveMeet / openMeet / confirmMeet (docs/prototype.html L540–545, L1291–1303,
 * L1924–1984); the Meetings redesign (goal-mvdhu5fs, contracts t-81825c59): NO AUDIO EVER REACHES THE SERVER. A
 * recording (live, or an audio file) is transcribed in the browser and the person edits the transcript before
 * saving; a minutes document is uploaded once and read during that request. The server's transcription,
 * re-transcription and stored-audio paths are gone; old meetings keep what they have (`media.kind` 'audio',
 * `transcript_state` 'failed', `retranscribed_from_id`) and are shown as they are.
 *
 * The flow, whatever the minutes came as:
 *   1. `POST /meetings` (typed minutes, or an edited transcript) or `POST /meetings/document` (a minutes file) makes
 *      a DRAFT. Typed minutes may carry the tasks and decisions the person already ticked (the "Find tasks" step);
 *      otherwise the worker extracts them (`extraction_state`), each proposed task quoting the line it came from.
 *   2. The person edits the draft (`PATCH`), then `POST /meetings/{id}/confirm` with a tick or a reason for every
 *      proposed task. Confirming CREATES the tasks (the prototype's bug, fixed), once: a repeat confirm returns
 *      the same result and creates nothing. An unticked task needs a reason and lands in Dropped tasks.
 * Nothing in the scheduler ever confirms a meeting (ADR §9).
 *
 * Visibility follows the client: managers all; an ops member the clients they are the current SPOC of (others 404).
 * Writes: a manager, or the client's SPOC. The audio itself plays only for the person who added it and managers
 * (prototype Settings → Recordings: "Audio is visible only to the person who added it, the Ops associate director
 * and founders"). Every write is audited (real actor + acting-as).
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, Version, page } from './common.js';
import { ClientRef } from './clients.js';
import { DropReason, DroppedItem, Task } from './tasks.js';
import { UserRef } from './users.js';
// ============================================================================ shared pieces
const Title = z.string().trim().min(3, 'Add a meeting title of at least 3 characters').max(160, 'Keep the title under 160 characters');
const TaskTitle = z.string().trim().min(3, 'Say what has to be done in a few words').max(200, 'Keep it under 200 characters');
const DecisionText = z.string().trim().min(3, 'Write the decision in a few words').max(300, 'Keep each decision under 300 characters');
const Note = z.string().trim().min(1).max(1000);
/**
 * How the minutes came in (ADR §11.10):
 *  - `typed` — typed or pasted minutes;
 *  - `upload` — a minutes FILE (PDF, Word, Excel, CSV, text, Markdown), read during `POST /meetings/document`;
 *  - `recorded` — a recording transcribed IN THE BROWSER (live, or an audio file) and edited by the person
 *    (`transcript_source`); optionally with a link to the recording held elsewhere.
 */
export const MeetingMediaKind = z.enum(['typed', 'upload', 'recorded']).meta({ id: 'MeetingMediaKind' });
export const MeetingStatus = z.enum(['draft', 'confirmed']).meta({ id: 'MeetingStatus' });
/**
 * Audio → text. The server no longer transcribes: a new meeting is `none` (typed or a document) or `ready` (a
 * browser transcript). `queued` / `running` / `failed` remain only on meetings made before the redesign (a
 * transcript still waiting then was closed as `failed`, with a `problem` saying to type the summary).
 */
export const TranscriptState = z.enum(['none', 'queued', 'running', 'ready', 'failed']).meta({ id: 'TranscriptState' });
/**
 * Where a meeting's minutes text came from, when it was not typed:
 *  - `live`: a short recording (up to 2 minutes) transcribed by the browser's standard speech recognition, on a
 *    `recorded` meeting (operator decision 2026-10-18; audio-file upload is dropped);
 *  - `document`: text read from a minutes document by `POST /meetings/document/read`, edited, and submitted on an
 *    `upload` meeting (design t-23861faf open question 1). The document itself is not kept.
 */
export const TranscriptSource = z.enum(['live', 'document']).meta({ id: 'TranscriptSource' });
/** The longest live recording, in seconds (operator decision 2026-10-18). */
export const MEETING_LIVE_MAX_SECONDS = 120;
/**
 * Text → summary, decisions and proposed tasks (ADR-004 `meeting_summary`): `none` (the person gave them, or
 * nothing to read yet), `queued`, `running`, `ready`, `failed` (the draft stays with an empty summary for a person
 * to write — ADR §12).
 */
export const ExtractionState = z.enum(['none', 'queued', 'running', 'ready', 'failed']).meta({ id: 'ExtractionState' });
/** Prototype chips: "Minutes confirmed" / "Transcribing" / "Draft minutes". */
export const MEETING_STATUS_LABEL = { confirmed: 'Minutes confirmed', processing: 'Transcribing', draft: 'Draft minutes' };
// ============================================================================ the meeting
export const MeetingDecision = z
    .object({ id: Uuid, text: z.string(), sort_order: z.number().int() })
    .meta({ id: 'MeetingDecision' });
/**
 * A staged task (`meeting_tasks`). Before confirm: `accepted` null. After: `true` with `created_task_id`, or
 * `false` with `dropped_item_id` (the reason is on the dropped item).
 */
export const MeetingTask = z
    .object({
    id: Uuid,
    title: z.string(),
    /** Default: the client's current SPOC (prototype "Tasks from this meeting — go to {SPOC}"). */
    suggested_assignee: UserRef.nullable(),
    suggested_due: IsoDate.nullable(),
    /** Where it came from: typed in by a person, or proposed by the language model from the minutes/transcript. */
    source: z.enum(['person', 'extracted']),
    /** The sentence it came from (extracted only), ≤ 300 characters. */
    quote: z.string().nullable(),
    sort_order: z.number().int(),
    accepted: z.boolean().nullable(),
    created_task_id: Uuid.nullable(),
    dropped_item_id: Uuid.nullable(),
})
    .meta({ id: 'MeetingTask' });
/**
 * What is held for the meeting. `file` = the uploaded minutes document (opens for the person who added it and
 * managers); `link` = a recording held elsewhere ("Our system does not hold a copy"). `audio` = a recording we
 * stored — only on meetings made before the redesign; no new meeting stores audio. Typed minutes and a browser
 * transcript without a link have no media.
 */
export const MeetingMedia = z
    .object({
    kind: z.enum(['audio', 'link', 'file']),
    /** The original file name (audio or minutes file). */
    filename: z.string().nullable(),
    bytes: z.number().int().nonnegative().nullable(),
    duration_s: z.number().int().nonnegative().nullable(),
    /** A recording link the person gave (`link` only). */
    url: z.string().nullable(),
    uploaded_by: UserRef,
    /** `true` when the caller may play or open the stored file (`GET /meetings/{id}/media-url`). */
    can_open: z.boolean(),
    /** When the stored audio is deleted under the retention setting; `null` = kept forever (the default), or not audio. */
    delete_after: IsoDate.nullable(),
    /** `true` once the audio has been deleted under retention: the meeting stays, re-transcribing is no longer possible. */
    deleted: z.boolean(),
})
    .meta({ id: 'MeetingMedia' });
const MeetingBase = z.object({
    id: Uuid,
    client: ClientRef,
    title: z.string(),
    held_on: IsoDate,
    /** Who logged it (prototype "by {first}"). */
    held_by: UserRef,
    /** Free text, as the prototype ("Farah (client), our SPOC, our analyst"). */
    attendees: z.string().nullable(),
    media_kind: MeetingMediaKind,
    /** `recorded` only: `live` (transcribed in the browser); `null` for typed, documents, link only and older meetings. */
    transcript_source: TranscriptSource.nullable(),
    /** Length in minutes of the audio the browser transcribed (from `duration_s`); `null` for typed minutes and documents. */
    length_minutes: z.number().int().nonnegative().nullable(),
    status: MeetingStatus,
    transcript_state: TranscriptState,
    extraction_state: ExtractionState,
    /** One or two sentences; `null` until written or extracted. */
    summary: z.string().nullable(),
    decisions_count: z.number().int().nonnegative(),
    proposed_tasks_count: z.number().int().nonnegative(),
    /** Tasks this meeting created on confirm (prototype stat "Tasks created from minutes"). */
    created_tasks_count: z.number().int().nonnegative(),
    confirmed_by: UserRef.nullable(),
    confirmed_at: Timestamp.nullable(),
    /** Older meetings only: a re-transcription was a new draft pointing at the meeting it was made from. */
    retranscribed_from_id: Uuid.nullable(),
    /** The caller may edit and confirm this draft (a manager, or the client's SPOC; never once confirmed). */
    can_edit: z.boolean(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
});
/** One row of the Meetings tab (`meetList`). */
export const MeetingListItem = MeetingBase.extend({ media: MeetingMedia.nullable() }).meta({ id: 'MeetingListItem' });
/** The meeting drawer: everything, including the minutes text and the staged tasks. */
export const Meeting = MeetingBase.extend({
    media: MeetingMedia.nullable(),
    /** The minutes as typed, the edited transcript, or the text read from the document. */
    minutes_text: z.string().nullable(),
    decisions: z.array(MeetingDecision),
    proposed_tasks: z.array(MeetingTask),
    /** Shown on a failed transcript or extraction: what went wrong and what to do (guide §9). */
    problem: z.string().nullable(),
}).meta({ id: 'Meeting' });
// ============================================================================ GET /meetings
export const ListMeetingsQuery = PageQuery.extend({
    client_id: Uuid.optional(),
    status: MeetingStatus.optional(),
    /** `transcribing` = a meeting whose extraction (or, on an older meeting, transcript) is still queued or running. */
    state: z.enum(['transcribing', 'failed']).optional(),
    /** Held on or after / on or before (both inclusive). */
    from: IsoDate.optional(),
    to: IsoDate.optional(),
}).strict();
/** The tab's four stats, within what the caller sees and the filters' client. */
export const MeetingStats = z
    .object({
    this_month: z.number().int().nonnegative(),
    awaiting_confirmation: z.number().int().nonnegative(),
    tasks_created: z.number().int().nonnegative(),
    decisions_logged: z.number().int().nonnegative(),
})
    .meta({ id: 'MeetingStats' });
export const MeetingPage = page(MeetingListItem).extend({ stats: MeetingStats }).meta({ id: 'MeetingPage' });
// ============================================================================ limits
/** `POST /meetings/document/read`: one file up to 8 MB (operator, 18 Oct; above: `422 validation_failed` on `file`, never a 413). */
export const MINUTES_FILE_MAX_BYTES = 8 * 1024 * 1024;
export const MINUTES_FILE_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.csv', '.txt', '.md'];
/** A presigned PUT straight to storage; nothing goes through the API. No row is written until the object exists (ADR §12). */
export const UploadSlot = z
    .object({
    /** Send back as `file_key` (request and document files). Bound to the caller. */
    key: z.string().min(1).max(500),
    url: z.string(),
    method: z.literal('PUT'),
    /** Headers the PUT must carry (Content-Type and any signature headers). */
    headers: z.record(z.string(), z.string()),
    expires_at: Timestamp,
})
    .meta({ id: 'UploadSlot' });
// ============================================================================ POST /meetings
/** A task the person already has in mind (typed minutes "Find tasks" / "+ Add a task"). */
export const MeetingTaskInput = z
    .object({
    title: TaskTitle,
    /** Someone other than the client's SPOC needs `task.reassign` (prototype: only the head picks the person); else `403`. */
    suggested_assignee_id: Uuid.optional(),
    suggested_due: IsoDate.optional(),
})
    .strict();
const MeetingCommon = {
    client_id: Uuid,
    /** Not in the future. The prototype's "Today / Yesterday / 2 days ago / Last week" picker sends the date. */
    held_on: IsoDate,
    title: Title,
    attendees: z.string().trim().max(500).optional(),
};
const MinutesText = z.string().trim().min(10, 'Write, say or upload the minutes').max(100_000, 'Keep the minutes under 100,000 characters');
/**
 * Typed minutes. With `proposed_tasks` and/or `decisions` the draft holds exactly those and nothing is extracted;
 * without either, the worker extracts them from `minutes_text` (`extraction_state: 'queued'`).
 */
const CreateTypedMeeting = z
    .object({
    ...MeetingCommon,
    media_kind: z.literal('typed'),
    minutes_text: MinutesText,
    summary: z.string().trim().max(1000).optional(),
    decisions: z.array(DecisionText).max(50).optional(),
    proposed_tasks: z.array(MeetingTaskInput).max(50).optional(),
})
    .strict();
const RecordingUrl = z.url({ protocol: /^https$/, message: 'Paste the https:// link to the recording' }).max(2000);
/**
 * `media_kind: 'recorded'` has two forms, told apart by `transcript_source` (contract revision t-b9332deb):
 *  - a BROWSER TRANSCRIPT (`transcript_source: 'live'`): a live recording transcribed in the browser that the
 *    person has read and corrected. `minutes_text` is that EDITED transcript; the draft is `transcript_state:
 *    'ready'` and the worker extracts the summary, decisions and tasks from it. No audio is sent or stored.
 *    `consent_confirmed` must be `true` while `recordings.require_consent` is on (`422` on `consent_confirmed`).
 *    `recording_url` is optional.
 *  - LINK ONLY (no `transcript_source`): a recording held elsewhere ("Our system does not hold a copy").
 *    `recording_url` is required and `minutes_text` is not allowed; nothing is read or extracted, the person writes
 *    the summary and tasks. No consent is asked (decision 7). The v1 body, unchanged.
 */
const CreateRecordedMeeting = z
    .object({
    ...MeetingCommon,
    media_kind: z.literal('recorded'),
    transcript_source: z.literal('live').optional(),
    minutes_text: MinutesText.optional(),
    consent_confirmed: z.boolean().optional(),
    /** Browser transcript: length of the live recording, in whole seconds (at most 2 minutes). */
    duration_s: z.number().int().nonnegative().max(MEETING_LIVE_MAX_SECONDS, 'A live recording is at most 2 minutes').optional(),
    /** A link to the recording held elsewhere: required for link only, optional with a browser transcript. */
    recording_url: RecordingUrl.optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.transcript_source) {
        if (b.minutes_text === undefined)
            ctx.addIssue({ code: 'custom', path: ['minutes_text'], message: 'Add the transcript' });
        return;
    }
    // Link only.
    if (!b.recording_url)
        ctx.addIssue({ code: 'custom', path: ['recording_url'], message: 'Paste the https:// link to the recording' });
    for (const k of ['minutes_text', 'duration_s']) {
        if (b[k] !== undefined) {
            ctx.addIssue({ code: 'custom', path: [k], message: 'Only a live recording transcribed in the browser carries this' });
        }
    }
});
const DocumentFilename = z.string().trim().min(1).max(200).refine((f) => MINUTES_FILE_EXTENSIONS.some((e) => f.toLowerCase().endsWith(e)), { message: 'Use a PDF, Word, Excel, CSV, text or Markdown file' });
/**
 * Minutes from a DOCUMENT: the text `POST /meetings/document/read` returned, as the person edited it, plus the
 * document's file name. The draft is `transcript_state: 'none'` with extraction queued; no consent is asked; the
 * document itself was never stored (the meeting shows its name only: `media.kind: 'file'`, `can_open: false`).
 */
const CreateDocumentMeeting = z
    .object({
    ...MeetingCommon,
    media_kind: z.literal('upload'),
    transcript_source: z.literal('document'),
    document_filename: DocumentFilename,
    minutes_text: MinutesText,
})
    .strict();
export const CreateMeetingBody = z.discriminatedUnion('media_kind', [CreateTypedMeeting, CreateRecordedMeeting, CreateDocumentMeeting]);
// ============================================================================ POST /meetings/document/read (multipart)
/**
 * The file part of `POST /meetings/document/read`, as the API hands it to the route after reading the multipart body
 * (the browser sends an ordinary `multipart/form-data` file field named `file`).
 */
export const MinutesFileUpload = z
    .object({
    filename: z.string().trim().min(1).max(200),
    content_type: z.string().max(200),
    bytes: z.number().int().nonnegative(),
    data: z.instanceof(Uint8Array),
})
    .strict();
/**
 * Read a minutes document: ONE file (`.pdf`, `.docx`, `.xlsx`, `.csv`, `.txt`, `.md`, at most 8 MB) as
 * `multipart/form-data`. Read-only: it makes no meeting and stores nothing. Every refusal is `422 validation_failed`
 * with a field error on `file` saying which — another file type, over 8 MB, no readable text (a scanned or empty PDF,
 * an empty or unopenable file), or text over 100,000 characters. Nothing is ever cut.
 */
export const ReadMinutesDocumentBody = z
    .object({
    file: MinutesFileUpload.refine((f) => MINUTES_FILE_EXTENSIONS.some((e) => f.filename.toLowerCase().endsWith(e)), {
        message: 'Use a PDF, Word, Excel, CSV, text or Markdown file',
    }),
})
    .strict();
/** The document's text (line breaks kept, so the person can edit it and a task can quote its line). */
export const ReadMinutesDocumentResponse = z
    .object({
    text: z.string(),
    chars: z.number().int().nonnegative(),
    filename: z.string(),
})
    .meta({ id: 'ReadMinutesDocumentResponse' });
// ============================================================================ PATCH /meetings/{id} (draft only)
/** A staged task in an edit: `id` keeps an existing one (and its quote); without `id` it is new. */
export const MeetingTaskEdit = MeetingTaskInput.extend({ id: Uuid.optional() }).strict();
/**
 * Edit the draft before confirming. `decisions` and `proposed_tasks`, when sent, REPLACE the lists (a staged task
 * left out is removed). A confirmed meeting is `409` ("Confirmed minutes cannot be changed").
 */
export const UpdateMeetingBody = z
    .object({
    title: Title.optional(),
    held_on: IsoDate.optional(),
    attendees: z.string().trim().max(500).nullable().optional(),
    summary: z.string().trim().max(1000).nullable().optional(),
    decisions: z.array(DecisionText).max(50).optional(),
    proposed_tasks: z.array(MeetingTaskEdit).max(50).optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), { message: 'Change at least one thing' });
// ============================================================================ POST /meetings/{id}/confirm
/**
 * One line per staged task — every staged task must appear exactly once (`422` names the missing one). Ticked:
 * the task is created for `assignee_id` (default the suggested person, else the client's SPOC; someone other than
 * the SPOC needs `task.reassign`) due `due` (default the suggested date, else in 3 days). Unticked: needs
 * `drop_reason_code` (and `drop_note` for Other) — it lands in Dropped tasks as "Meeting minutes".
 */
export const ConfirmMeetingTask = z
    .object({
    meeting_task_id: Uuid,
    accept: z.boolean(),
    title: TaskTitle.optional(),
    due: IsoDate.optional(),
    assignee_id: Uuid.optional(),
    drop_reason_code: DropReason.optional(),
    drop_note: Note.optional(),
})
    .strict()
    .superRefine((t, ctx) => {
    if (!t.accept && !t.drop_reason_code) {
        ctx.addIssue({ code: 'custom', path: ['drop_reason_code'], message: 'Say why this task is left out' });
    }
    if (!t.accept && t.drop_reason_code === 'other' && !t.drop_note) {
        ctx.addIssue({ code: 'custom', path: ['drop_note'], message: 'Add a note when the reason is Other' });
    }
});
export const ConfirmMeetingBody = z
    .object({ tasks: z.array(ConfirmMeetingTask).max(50) })
    .strict()
    .refine((b) => new Set(b.tasks.map((t) => t.meeting_task_id)).size === b.tasks.length, {
    path: ['tasks'],
    message: 'Each task can appear only once',
});
/**
 * The confirmed meeting with what confirming made. A repeat confirm of a confirmed meeting returns this same
 * result (the tasks and dropped items made the first time) and creates nothing — whatever its body says.
 */
export const ConfirmMeetingResponse = z
    .object({ meeting: Meeting, created_tasks: z.array(Task), dropped: z.array(DroppedItem) })
    .meta({ id: 'ConfirmMeetingResponse' });
// ============================================================================ media
/** A short-lived same-origin link (`/api/v1/files/{token}`, see `files.get`) to open the minutes document (or an older meeting's audio). */
export const MediaUrlResponse = z
    .object({ url: z.string(), expires_at: Timestamp, content_type: z.string().nullable(), filename: z.string().nullable() })
    .meta({ id: 'MediaUrlResponse' });
//# sourceMappingURL=meetings.js.map